/**
 * Kill switch for the keep-warm LOOP (L-0868, ORCH-28 2026-10-15: "one config flag turns the loop off, so it can be
 * stopped without a deploy"). Firebase Remote Config key `wake_keep_warm_enabled`:
 *  - not published (the default) -> ON;
 *  - published as false          -> OFF: no repeat; one pass on app open and on each module / screen change, which is
 *                                   the one-shot behaviour from before L-0868. The build flag REACT_APP_WAKE_KNOCKS=off
 *                                   still turns every knock off.
 * A failed read keeps the last value (initially ON): a knock is harmless (OPTIONS only, no handler runs), so a Remote
 * Config hiccup must not change behaviour either way; a published OFF stays OFF.
 *
 * FRESHNESS: the fetch interval is lowered to 5 minutes and the store re-reads on that cadence and when the tab is shown,
 * so an OFF reaches an open tab within about 5 minutes without a reload (same shape as useAiShortDescriptionSwitch).
 */
import { useEffect, useState } from 'react';
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';

export const KEEP_WARM_SWITCH_KEY = 'wake_keep_warm_enabled';
export const KEEP_WARM_SWITCH_REFRESH_MS = 5 * 60 * 1000;
/** After a failed read, re-reads triggered by tab shows wait this long (the 5-minute timer still runs), so blocked storage
 * does not mean a failing fetch and a warning on every tab switch. */
export const KEEP_WARM_SWITCH_RETRY_AFTER_FAILURE_MS = 60_000;

type Listener = (on: boolean) => void;
const listeners = new Set<Listener>();
let current = true;
let inFlight: Promise<void> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let failedAt = 0;

async function readSwitch(): Promise<boolean> {
  try {
    const remoteConfig = getRemoteConfig();
    remoteConfig.defaultConfig = { ...remoteConfig.defaultConfig, [KEEP_WARM_SWITCH_KEY]: true };
    remoteConfig.settings.minimumFetchIntervalMillis = KEEP_WARM_SWITCH_REFRESH_MS;
    await fetchAndActivate(remoteConfig);
    failedAt = 0;
    return getValue(remoteConfig, KEEP_WARM_SWITCH_KEY).asBoolean();
  } catch (error) {
    failedAt = Date.now();
    // A warn (not an error) keeps a Remote Config storage hiccup out of the error-reporting bridge.
    console.warn('[KeepWarmSwitch] Remote Config unavailable, keeping the last value:', error);
    return current;
  }
}

function publish(on: boolean): void {
  current = on;
  listeners.forEach((l) => l(on));
}

/** One shared re-read, de-duplicated while in flight (Remote Config itself also throttles to the 5-minute interval). */
function refresh(fromTimer = false): Promise<void> {
  if (inFlight) return inFlight;
  if (!fromTimer && failedAt && Date.now() - failedAt < KEEP_WARM_SWITCH_RETRY_AFTER_FAILURE_MS) return Promise.resolve();
  inFlight = readSwitch().then(publish).finally(() => { inFlight = null; });
  return inFlight;
}

const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };

function start(): void {
  if (timer) return;
  timer = setInterval(() => { void refresh(true); }, KEEP_WARM_SWITCH_REFRESH_MS);
  document.addEventListener('visibilitychange', onVisible);
}

function stop(): void {
  if (timer) clearInterval(timer);
  timer = null;
  document.removeEventListener('visibilitychange', onVisible);
}

/** Test seam: forget everything. */
export function resetKeepWarmSwitchForTests(): void {
  stop();
  listeners.clear();
  current = true;
  inFlight = null;
  failedAt = 0;
}

/** True unless Remote Config says the keep-warm loop is off. */
export function useKeepWarmSwitch(): boolean {
  const [on, setOn] = useState(current);
  useEffect(() => {
    listeners.add(setOn);
    start();
    void refresh();
    return () => {
      listeners.delete(setOn);
      if (listeners.size === 0) stop();
    };
  }, []);
  return on;
}
